import React, { useState } from "react";
import { Modal, Table } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import { API_URL } from "../../../../../constants";
import {
  PostFormServiceCaller,
  PostServiceCaller,
  DeleteServiceCaller,
  GetServiceCaller,
} from "../../../../../services/serviceCaller";

const BulkUploadOtherDeductionModal = ({ show, onClose, onImported, payrollList }) => {
  const [payrollMonthID, setPayrollMonthID] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [stagedRows, setStagedRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);

  const resetState = () => {
    setPayrollMonthID(null);
    setSelectedFile(null);
    setStagedRows([]);
    setSummary(null);
  };

  const handleClose = async () => {
    if (stagedRows.length > 0 && payrollMonthID) {
      try {
        await DeleteServiceCaller(
          `${API_URL}payroll/incentive&CCA/other-deduction/discard`,
          {},
          "delete"
        );
      } catch (error) {
        console.error("Error discarding staged Other Deduction rows:", error);
      }
    }
    resetState();
    onClose();
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0] || null);
    setStagedRows([]);
    setSummary(null);
  };

  const handleUpload = async () => {
    if (!payrollMonthID) {
      toast.error("Please select a Payroll Month.");
      return;
    }
    if (!selectedFile) {
      toast.error("Please select a CSV file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("csv", selectedFile);
    formData.append("PayrollMonthID", payrollMonthID);

    try {
      setUploading(true);
      const response = await PostFormServiceCaller(
        `${API_URL}payroll/incentive&CCA/other-deduction/import-csv`,
        formData,
        "post"
      );

      if (response?.status === 200) {
        setStagedRows(response?.data?.data || []);
        setSummary({
          matched: response?.data?.matched || 0,
          errors: response?.data?.errors || 0,
        });
      }
    } catch (error) {
      console.error("Error uploading Other Deduction CSV:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    try {
      setImporting(true);
      const response = await PostServiceCaller(
        `${API_URL}payroll/incentive&CCA/other-deduction/confirm`,
        { PayrollMonthID: payrollMonthID },
        "post"
      );

      if (response?.status === 200) {
        toast.success(response?.data?.message || "Imported successfully");
        resetState();
        onImported && onImported();
        onClose();
      }
    } catch (error) {
      console.error("Error confirming Other Deduction import:", error);
    } finally {
      setImporting(false);
    }
  };

  const downloadSample = async () => {
    try {
      const fileUrl = await GetServiceCaller(
        `${API_URL}payroll/incentive&CCA/other-deduction/sample`
      );
      const url = fileUrl?.data?.data;
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = "OtherDeductionSample.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading Other Deduction sample:", error);
    }
  };

  const matchedCount = summary?.matched ?? 0;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Bulk Upload Other Deduction</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-5">
            <label className="form-label">
              Payroll Month<span className="required">*</span>
            </label>
            <Select
              value={payrollList?.find((o) => o.value === payrollMonthID) || null}
              options={payrollList}
              onChange={(option) => setPayrollMonthID(option?.value ?? null)}
              placeholder="Select"
            />
          </div>
          <div className="col-md-5">
            <label className="form-label">
              CSV File<span className="required">*</span>
            </label>
            <input
              type="file"
              accept=".csv"
              className="form-control"
              onChange={handleFileChange}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button
              type="button"
              className="custom-btn btn-sm w-100"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? <FaSpinner className="sm-spinner" /> : "Upload"}
            </button>
          </div>
        </div>

        <div className="mt-2">
          <button type="button" className="btn btn-link p-0" onClick={downloadSample}>
            Download sample CSV template
          </button>
        </div>

        {stagedRows.length > 0 && (
          <>
            <div className="mt-3">
              <strong>{matchedCount}</strong> row(s) matched,{" "}
              <strong className={summary?.errors ? "text-danger" : ""}>
                {summary?.errors ?? 0}
              </strong>{" "}
              row(s) with errors.
            </div>
            <div className="mt-2" style={{ maxHeight: "350px", overflowY: "auto" }}>
              <Table bordered striped size="sm">
                <thead>
                  <tr>
                    <th>EMP ID</th>
                    <th>Name</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stagedRows.map((row) => (
                    <tr key={row.ID} className={row.Status === "error" ? "table-danger" : ""}>
                      <td>{row.EmpCode}</td>
                      <td>{row.EmployeeName}</td>
                      <td>{row.Amount}</td>
                      <td>{row.Status === "error" ? row.ErrorMessage : "Matched"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="cancel-btn me-2" onClick={handleClose}>
          Cancel
        </button>
        <button
          type="button"
          className="custom-btn"
          onClick={handleConfirmImport}
          disabled={importing || matchedCount === 0}
        >
          {importing ? <FaSpinner className="sm-spinner" /> : "Confirm Import"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUploadOtherDeductionModal;
